import { parseMarkdown } from './simple-markdown.js';

/**
 * Universal Markdown Renderer
 * Automatically processes all .markdown-content containers
 * Supports mixed HTML and Markdown content
 */
class MarkdownRenderer {
    constructor(options = {}) {
        this.options = {
            selector: '.markdown-content',
            autoInit: true,
            preserveHtml: true,
            ...options
        };

        if (this.options.autoInit) {
            this.init();
        }
    }

    /**
     * Initialize the renderer
     */
    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.processAll());
        } else {
            this.processAll();
        }
    }

    /**
     * Process all markdown containers on the page
     */
    processAll() {
        const containers = document.querySelectorAll(this.options.selector);
        console.log(`🔍 Found ${containers.length} markdown container(s)`);

        containers.forEach((container, index) => {
            this.processContainer(container, index);
        });
    }

    /**
     * Process a single container
     * @param {HTMLElement} container 
     * @param {number} index 
     */
    async processContainer(container, index) {
        try {
            // Get content from various sources
            const content = await this.getContent(container);
            
            if (content && content.trim()) {
                this.renderContent(content, container);
                console.log(`✅ Rendered content for container ${index + 1}`);
            } else {
                console.log(`⚠️ No content found in container ${index + 1}`);
            }
        } catch (error) {
            console.error(`❌ Error processing container ${index + 1}:`, error);
            this.showError(container, error.message);
        }
    }

    /**
     * Get content from various sources
     * @param {HTMLElement} container 
     * @returns {Promise<string>}
     */
    async getContent(container) {
        // Priority 1: data-src attribute (fetch from URL)
        if (container.dataset.src) {
            return await this.fetchContent(container.dataset.src);
        }

        // Priority 2: data-markdown attribute
        if (container.dataset.markdown) {
            return container.dataset.markdown;
        }

        // Priority 3: textContent
        const textContent = container.textContent.trim();
        if (textContent && !this.isLoadingPlaceholder(textContent)) {
            // Check if this looks like markdown content
            if (this.containsMarkdown(textContent)) {
                return textContent;
            }
        }

        // Priority 4: innerHTML content (mixed HTML/markdown)
        const innerHTML = container.innerHTML.trim();
        if (innerHTML && !this.isLoadingPlaceholder(innerHTML)) {
            return innerHTML;
        }

        return null;
    }

    /**
     * Check if content is a loading placeholder
     * @param {string} content 
     * @returns {boolean}
     */
    isLoadingPlaceholder(content) {
        const lowercaseContent = content.toLowerCase();
        return lowercaseContent.includes('loading') || 
               lowercaseContent.includes('загрузка') ||
               content.includes('text-white/60');
    }

    /**
     * Fetch content from a URL
     * @param {string} url 
     * @returns {Promise<string>}
     */
    async fetchContent(url) {
        console.log(`📥 Fetching content from: ${url}`);
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch content: ${response.statusText}`);
        }
        return await response.text();
    }

    /**
     * Render content (mixed HTML and Markdown) into a container
     * @param {string} content 
     * @param {HTMLElement} container 
     */
    renderContent(content, container) {
        let processedContent = content;

        const hasHtmlTags = /<[^>]+>/g.test(content);
        
        if (!hasHtmlTags || this.isPureMarkdown(content)) {
            // Treat as pure markdown
            processedContent = parseMarkdown(content);
        } else if (this.options.preserveHtml) {
            // Process content that may contain both HTML and Markdown
            processedContent = this.processHtmlAndMarkdown(content);
        } else {
            // Treat everything as pure markdown
            processedContent = parseMarkdown(content);
        }

        container.innerHTML = processedContent;

        // Add syntax highlighting for code blocks
        this.enhanceCodeBlocks(container);

        // Dispatch custom event
        container.dispatchEvent(new CustomEvent('markdownRendered', {
            detail: { originalContent: content, processedContent }
        }));
    }

    /**
     * Process content that may contain both HTML and Markdown
     * @param {string} content 
     * @returns {string}
     */
    processHtmlAndMarkdown(content) {
        // Create a temporary container to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;

        // Process text nodes for markdown
        this.processTextNodes(tempDiv);

        return tempDiv.innerHTML;
    }

    /**
     * Recursively process text nodes to convert markdown
     * @param {HTMLElement} element 
     */
    processTextNodes(element) {
        const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );

        const textNodes = [];
        let node;
        
        // Collect text nodes first to avoid modifying during iteration
        while (node = walker.nextNode()) {
            textNodes.push(node);
        }

        // Process each text node
        textNodes.forEach(textNode => {
            const text = textNode.textContent.trim();
            
            // Skip if text is empty or parent is already a formatted element
            if (!text || this.isFormattedElement(textNode.parentElement)) {
                return;
            }

            // Check if text contains markdown patterns
            if (this.containsMarkdown(text)) {
                const markdownHtml = parseMarkdown(text);
                
                // Replace text node with parsed HTML
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = markdownHtml;
                
                // Move all child nodes to replace the text node
                while (tempDiv.firstChild) {
                    textNode.parentElement.insertBefore(tempDiv.firstChild, textNode);
                }
                textNode.remove();
            }
        });
    }

    /**
     * Check if element is already formatted (code, pre, etc.)
     * @param {HTMLElement} element 
     * @returns {boolean}
     */
    isFormattedElement(element) {
        const formattedTags = ['code', 'pre', 'script', 'style', 'textarea'];
        return formattedTags.includes(element.tagName.toLowerCase());
    }

    /**
     * Check if text contains markdown patterns
     * @param {string} text 
     * @returns {boolean}
     */
    containsMarkdown(text) {
        const markdownPatterns = [
            /^\s*#{1,6}\s+/m,     // Headers
            /\*\*.*?\*\*/,        // Bold with asterisks
            /(?<!\*)\*(?!\*).*?(?<!\*)\*(?!\*)/,  // Italic with asterisks (not bold)
            /__.*?__/,            // Bold with underscores
            /(?<!_)_(?!_).*?(?<!_)_(?!_)/,        // Italic with underscores (not bold)
            /`[^`]+`/,            // Inline code
            /```[\s\S]*?```/,     // Code blocks
            /^\s*[-*+]\s+/m,      // Unordered lists
            /^\s*\d+\.\s+/m,      // Ordered lists
            /^\s*>\s+/m,          // Blockquotes
            /\[.*?\]\(.*?\)/,     // Links
            /^\s*---+\s*$/m       // Horizontal rules
        ];

        return markdownPatterns.some(pattern => pattern.test(text));
    }

    /**
     * Check if content is primarily markdown (even if it has some HTML)
     * @param {string} content 
     * @returns {boolean}
     */
    isPureMarkdown(content) {
        // If content has markdown headers, it's likely primarily markdown
        return /^\s*#{1,6}\s+/m.test(content);
    }

    /**
     * Enhance code blocks with syntax highlighting classes
     * @param {HTMLElement} container 
     */
    enhanceCodeBlocks(container) {
        const codeBlocks = container.querySelectorAll('pre code[data-language]');
        codeBlocks.forEach(block => {
            block.classList.add('language-' + block.dataset.language);
        });
    }

    /**
     * Show error state
     * @param {HTMLElement} container 
     * @param {string} message 
     */
    showError(container, message) {
        container.innerHTML = `
            <div class="text-center py-8">
                <div class="text-red-400">Error loading content</div>
                ${message ? `<div class="text-red-300 text-sm mt-2">${message}</div>` : ''}
            </div>
        `;
    }

    /**
     * Manually render content in a specific container
     * @param {string} content 
     * @param {string|HTMLElement} target 
     */
    render(content, target) {
        const container = typeof target === 'string' 
            ? document.querySelector(target) 
            : target;

        if (!container) {
            console.error('Target container not found:', target);
            return;
        }

        this.renderContent(content, container);
    }

    /**
     * Refresh all containers
     */
    refresh() {
        this.processAll();
    }
}

// Global API
window.MarkdownRenderer = MarkdownRenderer;

// Auto-initialize default instance
const defaultRenderer = new MarkdownRenderer();

// Export for module usage
export default MarkdownRenderer;
export { defaultRenderer }; 